import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'antd';
import {
  addR1EmailToNotificationListRequestSaga,
  changeR1NotifyOnBannerRequestSaga,
  changeR1NotifyOnCountryBannerRequestSaga,
  changeR1NotifyOnCountryPromotionRequestSaga,
  changeR1NotifyOnPromotionRequestSaga,
  getR1Network,
  getR1NetworkRequestSaga,
  removeR1EmailFromNotificationListRequestSaga,
  setPromotionSettings,
} from 'domains/r1Network/R1Network.store';
import { PromotionSettings, R1NetworkState } from 'domains/r1Network/R1Network.types';
import { getUserCommercialLink } from 'domains/user';
import R1NetworkReturnFeature from 'pages/BackOfficePage/BackOfficeCategories/R1Network/R1NetworkReturnFeature';
import { Box, CircleCrossButton, Flex, Pipeline, PlusCircleButton, Text } from 'UI';
import { Switch } from 'UI/Switch';
import { SInput, SMailOutlined, STag, SText } from './R1Network.styled';

export const useR1NetworkData = (): R1NetworkState => {
  const dispatch = useDispatch();
  const r1Network = useSelector(getR1Network);
  const commercialLink = useSelector(getUserCommercialLink);
  const birCode = commercialLink?.birCode;

  useEffect(() => {
    if (birCode) {
      dispatch(getR1NetworkRequestSaga());
    }
  }, [dispatch, birCode]);

  return r1Network;
};
// ... existing code ...
const R1Network = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const r1Network = useR1NetworkData();
  const promotionSettings = r1Network.promotionSettings;

  const [emailInput, setEmailInput] = useState<string>('');
  const isEmailListEmpty = promotionSettings.notificationsEmails.length === 0;

  const updateSettings = (partial: Partial<PromotionSettings>) => {
    if (!promotionSettings) {
      return;
    }
    const updated: PromotionSettings = { ...promotionSettings, ...partial };
    dispatch(setPromotionSettings(updated));
  };

  const handleAddEmail = () => {
    const email = emailInput.trim();
    if (!email || !promotionSettings) {
      return;
    }
    if (promotionSettings.notificationsEmails.includes(email)) {
      return;
    }
    dispatch(addR1EmailToNotificationListRequestSaga({ email }));
    updateSettings({
      notificationsEmails: [...promotionSettings.notificationsEmails, email],
    });
    setEmailInput('');
  };

  const handleRemoveMail = (email: string) => {
    if (email.length === 0 || promotionSettings.notificationsEmails.length === 0) {
      return;
    }
    dispatch(removeR1EmailFromNotificationListRequestSaga({ email }));
    updateSettings({
      notificationsEmails: promotionSettings.notificationsEmails.filter((mail) => mail !== email),
    });
  };

  const handleToggle = (
    key: keyof Pick<
      PromotionSettings,
      | 'isPromotionNotificationEnabled'
      | 'isBannerNotificationEnabled'
      | 'isCountryPromotionNotificationEnabled'
      | 'isCountryBannerNotificationEnabled'
    >,
  ) => (val: boolean) => {
    updateSettings({ [key]: val });
    switch (key) {
      case 'isPromotionNotificationEnabled':
        return dispatch(changeR1NotifyOnPromotionRequestSaga({ isPromotionNotificationEnabled: val }));
      case 'isBannerNotificationEnabled':
        return dispatch(changeR1NotifyOnBannerRequestSaga({ isBannerNotificationEnabled: val }));
      case 'isCountryPromotionNotificationEnabled':
        return dispatch(changeR1NotifyOnCountryPromotionRequestSaga({ isCountryPromotionNotificationEnabled: val }));
      case 'isCountryBannerNotificationEnabled':
        return dispatch(changeR1NotifyOnCountryBannerRequestSaga({ isCountryBannerNotificationEnabled: val }));
      default:
        return null;
    }
  };

  const isHandleAddEmailDisabled = () =>
    !emailInput || form.getFieldError(['email']).length > 0 || form.isFieldsValidating(['email']);

  if (!promotionSettings) {
    return null;
  }

  const justifySpaceBetween = 'space-between';

  return (
    <>
      <Box height={60} />
      <Text type={'h1'}>{t('backoffice.r1_network.send_promotion.title', 'Send Promotion update')}</Text>
      <Text type="base">
        {t(
          'backoffice.r1_network.send_promotion.description',
          'Activate or disable the feature to automatically send email updates to your network' +
            ' when new promotions are published by your Country and/or your business.',
        )}
      </Text>
      <Box height={30} />
      <Text type="heading3">
        {t(
          'backoffice.r1_network.send_promotion.select.title',
          'Select for which promotions you would like to send updates',
        )}
      </Text>
      <Box height={30} />
      <Flex direction={'column'} justify={justifySpaceBetween} padding={20}>
        <Flex direction={'column'} justify={justifySpaceBetween}>
          <Text type="heading4">
            {t('backoffice.r1_network.send_promotion.select.promotion.title', 'Promotion created by my business')}
          </Text>
          <Box height={5} />
          <Flex justify={justifySpaceBetween}>
            <Text type="base">
              {t(
                'backoffice.r1_network.send_promotion.select.promotion.description',
                'Send an e-mail to my network when I create a promotion.',
              )}
            </Text>
            <Switch
              checked={promotionSettings.isPromotionNotificationEnabled}
              onChange={handleToggle('isPromotionNotificationEnabled')}
              disabled={isEmailListEmpty}
              data-cy={'promotion-created-by-my-business-notification-switch'}
            />
          </Flex>
        </Flex>
        <Box height={10} />
        <Pipeline offsetRatio={20} size={'100%'} horizontal />
        <Box height={10} />
        <Flex direction={'column'} justify={justifySpaceBetween}>
          <Text type="heading4">
            {t('backoffice.r1_network.send_promotion.select.banners.title', 'Banners created by my business')}
          </Text>
          <Box height={5} />
          <Flex justify={justifySpaceBetween}>
            <Text type="base">
              {t(
                'backoffice.r1_network.send_promotion.select.banners.description',
                'Send an e-mail to my network when I create a banner.',
              )}
            </Text>
            <Switch
              checked={promotionSettings.isBannerNotificationEnabled}
              onChange={handleToggle('isBannerNotificationEnabled')}
              disabled={isEmailListEmpty}
              data-cy={'banners-created-by-my-business-notification-switch'}
            />
          </Flex>
        </Flex>
        <Box height={10} />
        <Pipeline offsetRatio={20} size={'100%'} horizontal />
        <Box height={30} />
        <Flex direction={'column'} justify={justifySpaceBetween}>
          <Text type="heading4">
            {t(
              'backoffice.r1_network.send_promotion.select.promotion.country.title',
              'Promotion created by my Country',
            )}
          </Text>
          <Box height={5} />
          <Flex justify={justifySpaceBetween}>
            <Text type="base">
              {t(
                'backoffice.r1_network.send_promotion.select.promotion.country.description',
                'Send an e-mail to my network when a promotion is created by my Country.',
              )}
            </Text>
            <Switch
              checked={promotionSettings.isCountryPromotionNotificationEnabled}
              onChange={handleToggle('isCountryPromotionNotificationEnabled')}
              disabled={isEmailListEmpty}
              data-cy={'promotion-created-by-my-country-notification-switch'}
            />
          </Flex>
        </Flex>
        <Box height={30} />
        <Flex direction={'column'} justify={justifySpaceBetween}>
          <Text type="heading4">
            {t('backoffice.r1_network.send_promotion.select.banners.country.title', 'Banners created by my Country')}
          </Text>
          <Box height={5} />
          <Flex justify={justifySpaceBetween}>
            <Text type="base">
              {t(
                'backoffice.r1_network.send_promotion.select.banners.country.description',
                'Send an e-mail to my network when a banner is created by my Country.',
              )}
            </Text>
            <Switch
              checked={promotionSettings.isCountryBannerNotificationEnabled}
              onChange={handleToggle('isCountryBannerNotificationEnabled')}
              disabled={isEmailListEmpty}
              data-cy={'banners-created-by-my-country-notification-switch'}
            />
          </Flex>
        </Flex>
      </Flex>
      <Pipeline offsetRatio={40} size={'100%'} horizontal />
      <Box height={30} />
      <Text type="heading3">
        {t('backoffice.r1_network.send_promotion.mail.title', 'Uploader contact list or add e-mail address')}
      </Text>
      <Box height={16} />
      <Text type="base">{t('backoffice.r1_network.send_promotion.mail.subtitle', 'Add e-mail address')}</Text>
      <Box height={16} />
      <Flex direction={'row'}>
        <Form form={form} onFinish={handleAddEmail}>
          <Form.Item
            name="email"
            validateTrigger={['onPressEnter', 'onChangeDebounced']}
            rules={[
              {
                required: true,
                type: 'email',
                message: t(
                  'backoffice.r1_network.send_promotion.mail.subtitle.wrong_email_address',
                  'Please enter a valid email format, like name@example.com',
                ),
              },
              {
                validator: (_, value) => {
                  if (value && promotionSettings.notificationsEmails.includes(value.trim())) {
                    return Promise.reject(
                      new Error(
                        t(
                          'backoffice.r1_network.send_promotion.mail.subtitle.email_exists',
                          'This email is already on the list. Please enter a different one',
                        ),
                      ),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <SInput
              maxLength={256}
              value={emailInput}
              onChangeDebounced={setEmailInput}
              onBlur={setEmailInput}
              bordered
              placeholder={t('common.email.address.placeholder', 'Type your e-mail address here')}
              prefix={<SMailOutlined />}
              suffix={
                <PlusCircleButton disabled={isHandleAddEmailDisabled()} onClick={handleAddEmail} color={'grey40'} />
              }
              data-cy={'add-email-field'}
            ></SInput>
          </Form.Item>
        </Form>
      </Flex>
      <Box height={30} />
      <Text type="heading4">{t('backoffice.r1_network.send_promotion.mail_list.title', 'Emails addresses list')}</Text>
      <Box height={16} />

      <Flex gap={10} data-cy={'email-addresses-list'}>
        {promotionSettings.notificationsEmails.map<React.ReactNode>((value) => {
          return (
            <STag key={value}>
              <Flex direction={'row'} justify={'center'} align={'center'}>
                <SText type={'section'}>{value}</SText>
                <CircleCrossButton color={'grey40'} onClick={() => handleRemoveMail(value)} />
              </Flex>
            </STag>
          );
        })}
      </Flex>
      <Box height={120} />
      <Pipeline size={'100%'} horizontal />
      <R1NetworkReturnFeature />
    </>
  );
};

export default R1Network;
